import styled from '@emotion/styled'
import { SERVER_SIDE_DASHBOARD_OTHERS, ServerSideMetricsByTime } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { DateTime } from 'luxon'
import { FunctionComponent, useState } from 'react'
import { Color } from '~/assets/style/colors'
import Button from '~/components/Button'
import { ChartTooltipMetricProps, generateChartTooltip } from '~/components/ChartTooltip'
import { LegendProps } from '~/components/Legend'
import { normalizeAsTrackingLabel } from '~/components/layout/ProductTrackingIntegration'
import { TabProp, Tabs } from '~/components/Tabs'
import { TimelineMetricProps } from '~/components/TimelineMetric'
import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { AnalyticsChartWidget } from '~/modules/analytics/AnalyticsChartWidget'
import {
  serverSideAuctionCountObj,
  serverSideAuctionFillRateObj,
  serverSideAuctionRPMObj,
  serverSideBidCpmObj,
  serverSideBidDensityObj,
  serverSideBidRateObj,
  serverSideBidRequestsObj,
  serverSideBidTimeoutRateObj,
  serverSideeCPMObj,
  serverSideImpressionCountObj,
  serverSideImpressionRevenueObj,
} from '~/modules/analytics/common/formulas'
import { DATA_TYPES, getSortedKeysByServerSideImpression } from '~/modules/analytics/common/shared'
import { TRACKING_LABELS } from '~/modules/analytics/server-side/trackingLabels'
import { WithClassName } from '~/types/utils'
import { getChartTooltipDateFormat } from '~/utils/analytics'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'
import { downloadCSVData } from '~/utils/fileDownload'
import { aggregateRawDataMapping, aggregateRawDataValues } from '~/utils/object'
import { SERVER_SIDE_DIMENSION_COLORS, SERVER_SIDE_GENERAL_COLOR, ServerSideDimensions } from '~/utils/serverSide'

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

type ServerSideTimelineProps = WithClassName &
  Omit<WidgetProps, 'title' | 'info' | 'icon' | 'refreshTrackingLabel'> & {
    rawData: ServerSideMetricsByTime
    currencySymbol: CurrencySymbol
    onMetricChange: (metric: string) => void
    dimension: ServerSideDimensions
  }
type ServerSideFormulas =
  | typeof serverSideImpressionRevenueObj
  | typeof serverSideImpressionCountObj
  | typeof serverSideeCPMObj
  | typeof serverSideBidRateObj
  | typeof serverSideBidRequestsObj
  | typeof serverSideAuctionCountObj
  | typeof serverSideAuctionFillRateObj
  | typeof serverSideAuctionRPMObj
  | typeof serverSideBidDensityObj
  | typeof serverSideBidCpmObj
  | typeof serverSideBidTimeoutRateObj

type ServerSideMetrics = {
  name: string
  formula: ServerSideFormulas
  seriesType: 'bars' | 'line'
  tooltipText?: keyof typeof ANALYTICS_TOOLTIPS
  percentage: boolean
  breakdowns: ServerSideDimensions[]
}

type InputDataToConvertForChart = {
  rawData: ServerSideMetricsByTime
  currentFormula: ServerSideMetrics
  chartDataPoints: string[]
  currencySymbol: CurrencySymbol
  colors: { [key: string]: Color }
  type: 'percentage' | 'number'
}
const convertData = ({ rawData, currentFormula, chartDataPoints, currencySymbol, colors, type }: InputDataToConvertForChart) => {
  if (!rawData || !rawData.epoch.length) return []
  const dateFormat = getChartTooltipDateFormat(rawData.epoch)
  return rawData.epoch.map((epoch, index) => {
    const d = DateTime.fromFormat(epoch, 'yyyy-MM-dd HH:mm:ss')
    const result: (string | Date | number)[] = [
      `Date(${d.year}, ${d.month - 1}, ${d.day}, ${d.hour}, ${d.minute}, ${d.second})`,
      getChartTooltip(d, currentFormula, rawData, index, chartDataPoints, currencySymbol, colors, type, dateFormat),
      ...Object.entries((type === 'percentage' ? currentFormula.formula.percentage : currentFormula.formula.compute)(rawData as any, index))
        .sort((a, b) => chartDataPoints.indexOf(a[0]) - chartDataPoints.indexOf(b[0]))
        .map((e) => e[1]),
    ]
    return result
  })
}

const aggregateRawData = (rawData: ServerSideMetricsByTime): ServerSideMetricsByTime => {
  const keysToAggregate = getSortedKeysByServerSideImpression<ServerSideMetricsByTime>(rawData, SERVER_SIDE_DASHBOARD_OTHERS, false)

  let clonedData = JSON.parse(JSON.stringify(rawData))
  clonedData = Object.entries<{ [key: string]: number }[]>(clonedData)
    .filter((entry) => entry[0] !== 'epoch' && entry[0] !== 'mappings')
    .reduce((acc, [currentKey, currentValue]) => {
      const metricsValues = currentValue.map((v) => aggregateRawDataValues(v, keysToAggregate, SERVER_SIDE_DASHBOARD_OTHERS))
      return { ...acc, [currentKey]: metricsValues }
    }, {})
  clonedData.mappings = aggregateRawDataMapping(rawData.mappings, keysToAggregate, SERVER_SIDE_DASHBOARD_OTHERS)
  clonedData.epoch = [...rawData.epoch]
  return clonedData
}

const getChartTooltip = (
  epoch: DateTime,
  metricConfig: ServerSideMetrics,
  rawData: ServerSideMetricsByTime,
  index: number,
  chartDataPoints: string[],
  currencySymbol: CurrencySymbol,
  colors: { [p: string]: Color },
  type: 'percentage' | 'number',
  dateFormat: string
) => {
  const chartData = (type === 'percentage' ? metricConfig.formula.percentage : metricConfig.formula.compute)(rawData as any, index)
  const total = metricConfig.formula.sum(rawData as any, index)
  const metrics: ChartTooltipMetricProps[][] = [
    chartDataPoints.map((label) => ({
      label: rawData.mappings[label],
      value: (type === 'percentage' ? metricConfig.formula.displayablePercentage : metricConfig.formula.displayable)(chartData[label], currencySymbol).toString(),
      iconColor: colors[label],
    })),
    [{ label: metricConfig.formula.name, value: metricConfig.formula.displayable(total, currencySymbol).toString(), iconName: 'sigma' }],
  ]
  return generateChartTooltip({ date: epoch.toFormat(dateFormat), metrics })
}

const downloadCSV = (data: ServerSideMetricsByTime, dimension: ServerSideDimensions, metricsFormulas: ServerSideMetrics[]) => {
  const csvRows: string[] = []

  const metricNames = metricsFormulas.map((m) => m.formula.name)
  const header = ['epoch', dimension === 'overview' ? 'dimension' : dimension, ...metricNames]
  csvRows.push(header.join(','))

  Object.keys(data.mappings).forEach((mappingKey) => {
    data.epoch.forEach((epoch, index) => {
      const row = [
        epoch,
        data.mappings[mappingKey],
        ...metricsFormulas.map((metric) => {
          const computedValues = metric.formula.compute(data as any, index)
          return computedValues[mappingKey] ?? ''
        }),
      ]
      csvRows.push(row.join(','))
    })
  })

  downloadCSVData(csvRows.join('\n'), dimension === 'overview' ? 'server-side' : `server-side-by-${dimension}`)
}

const METRICS_FORMULAS: ServerSideMetrics[] = [
  {
    name: 'REVENUES',
    formula: serverSideImpressionRevenueObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_IMPRESSION_REVENUE',
    breakdowns: ['overview', 'adUnit', 'tagId', 'bidder'],
  },
  {
    name: 'AUCTIONS',
    formula: serverSideAuctionCountObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_AUCTION_COUNT',
    breakdowns: ['overview', 'adUnit', 'tagId'],
  },
  {
    name: 'BID_REQUESTS',
    formula: serverSideBidRequestsObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_AUCTION_COUNT',
    breakdowns: ['bidder'],
  },
  {
    name: 'IMPRESSIONS',
    formula: serverSideImpressionCountObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_IMPRESSIONS',
    breakdowns: ['overview', 'adUnit', 'tagId', 'bidder'],
  },
  {
    name: 'RPM',
    formula: serverSideAuctionRPMObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_IMPRESSION_RPM',
    breakdowns: ['overview'],
  },
  {
    name: 'FILL_RATE',
    formula: serverSideAuctionFillRateObj,
    seriesType: 'bars',
    percentage: true,
    tooltipText: 'SERVER_SIDE_FILL_RATE',
    breakdowns: ['overview'],
  },
  {
    name: 'ECPM',
    formula: serverSideeCPMObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'SERVER_SIDE_ECPM',
    breakdowns: ['overview', 'adUnit', 'tagId', 'bidder'],
  },
  {
    name: 'Bid Rate',
    formula: serverSideBidRateObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'SERVER_SIDE_BID_RATE',
    breakdowns: ['adUnit', 'tagId', 'bidder'],
  },
  {
    name: 'Bid Density',
    formula: serverSideBidDensityObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'SERVER_SIDE_BID_DENSITY',
    breakdowns: ['adUnit', 'tagId'],
  },
  {
    name: 'Bid CPM',
    formula: serverSideBidCpmObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'SERVER_SIDE_BIDDER_DETAILS_BID_CPM',
    breakdowns: ['bidder'],
  },
  {
    name: 'Timeout Rate',
    formula: serverSideBidTimeoutRateObj,
    seriesType: 'line',
    percentage: false,
    tooltipText: 'SERVER_SIDE_BIDDER_DETAILS_TIMEOUT_RATE',
    breakdowns: ['bidder'],
  },
]

export const ServerSideTimeline: FunctionComponent<ServerSideTimelineProps> = ({ rawData, onMetricChange, currencySymbol, dimension, ...props }) => {
  const data: ServerSideMetricsByTime = aggregateRawData(rawData)
  const [currentMetricIndex, setCurrentMetricIndex] = useState<number>(0)
  // Reset the selected metric when the dimension changes ("previous value" pattern).
  const [prevDimension, setPrevDimension] = useState(dimension)
  if (dimension !== prevDimension) {
    setPrevDimension(dimension)
    setCurrentMetricIndex(0)
  }
  const [typeTab, setTypeTab] = useState(0)
  const type = DATA_TYPES[typeTab].type
  const onTabClick = (tab: TabProp) => {
    const index = tabs.indexOf(tab)
    setTypeTab(index)
  }

  const currentMetric = METRICS_FORMULAS.filter((m) => m.breakdowns.includes(dimension))[currentMetricIndex]
  const tabs: TabProp[] = DATA_TYPES.map((type, index) => ({
    label: type.label,
    active: typeTab === index,
    disabled: type.type === 'percentage' && !currentMetric.percentage,
    trackingLabel: `ana-server-side-timeline-${type.type}-tab-apply`,
  }))

  const metrics: TimelineMetricProps[] = METRICS_FORMULAS.filter((m) => m.breakdowns.includes(dimension)).map((metric, index) => ({
    active: currentMetric.formula.name === metric.formula.name,
    label: metric.formula.name,
    value: `${metric.formula.displayable(metric.formula.sum(data as any), currencySymbol)}`,
    notApplicable: !metric.formula.isComputable(data),
    onClick: () => {
      onMetricChange && onMetricChange(metric.name)
      if (!metric.percentage) {
        setTypeTab(0)
      }
      metric.formula.isComputable(data) && setCurrentMetricIndex(index)
    },
    trackingLabel: `ana-server-side-timeline-metric-${normalizeAsTrackingLabel(metric.formula.name)}-apply`,
    tooltipText: metric.tooltipText ? ANALYTICS_TOOLTIPS[metric.tooltipText] : undefined,
  }))

  const chartDataPoints = getSortedKeysByServerSideImpression<ServerSideMetricsByTime>(data, SERVER_SIDE_DASHBOARD_OTHERS, false)
  const colors = chartDataPoints
    .filter((d) => d !== SERVER_SIDE_DASHBOARD_OTHERS)
    .reduce(
      (acc, d, i) => {
        acc[d] = dimension === 'overview' ? SERVER_SIDE_DIMENSION_COLORS[dimension][d] : SERVER_SIDE_DIMENSION_COLORS[dimension][i]
        return acc
      },
      {} as { [key: string]: Color }
    )

  if (data.mappings[SERVER_SIDE_DASHBOARD_OTHERS]) {
    colors[SERVER_SIDE_DASHBOARD_OTHERS] = SERVER_SIDE_GENERAL_COLOR.Others
  }

  const chartOptions = {
    ...AnalyticsDefaultChartOptions,
    colors: chartDataPoints.map((i) => colors[i]),
    seriesType: currentMetric.seriesType,
    isStacked: true,
    vAxis: type === 'percentage' ? { ...AnalyticsDefaultChartOptions.vAxis, format: "#'%'", minValue: 0 } : { ...AnalyticsDefaultChartOptions.vAxis },
  }

  const chartData = [
    [
      { type: 'datetime' },
      {
        type: 'string',
        role: 'tooltip',
        p: { html: true },
      },
      ...chartDataPoints,
    ],
    ...convertData({ rawData: data, currentFormula: currentMetric, chartDataPoints, currencySymbol, colors, type }),
  ]
  const legends: LegendProps[] = chartDataPoints.map((k) => ({ iconColor: colors[k], label: data.mappings[k] }))

  return (
    <AnalyticsChartWidget
      {...props}
      icon={'chart_bar'}
      title={'Performance'}
      metrics={metrics}
      legends={legends}
      chart={{ chartType: 'ComboChart', options: chartOptions, data: rawData ? chartData : undefined }}
      info={
        <InfoContainer>
          <Tabs tabs={tabs} fluid={false} onClick={onTabClick} />
          <Button
            variant={'tertiary'}
            iconName={'download'}
            onClick={() =>
              downloadCSV(
                data,
                dimension,
                METRICS_FORMULAS.filter((m) => m.breakdowns.includes(dimension))
              )
            }
            trackingLabel={TRACKING_LABELS.timeline.download}
          />
        </InfoContainer>
      }
      refreshTrackingLabel={TRACKING_LABELS.timeline.refresh}
    />
  )
}
